import React, { ReactElement, ReactNode, useEffect, useState } from 'react';

import parse, { HTMLReactParserOptions } from 'html-react-parser';
import { marked } from 'marked';
import { useParams, useSearchParams } from 'react-router-dom';

import { ReferencesDict } from '../../../backend/src/types/types';
import { referencingLinkRegex } from '@/constants/referencing';
import { useStore } from '@/store';

interface Reference {
  id?: string;
  type?: 'fact' | 'chunk';
}

// Component for grouping multiple references together
const ReferencesGroup = ({ references }: { references: ReactNode[] }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <span className="group relative inline-block">
      <span
        onClick={() => setExpanded(!expanded)}
        className="flex gap-1 rounded border border-blue-200 bg-blue-50 px-1 py-0 text-blue-500 transition-colors duration-150 hover:cursor-pointer hover:border-blue-300 hover:bg-blue-100 hover:text-blue-800"
      >
        {expanded ? references : '[...]'}
      </span>
    </span>
  );
};

/**
 * Unified function to format references in text
 * Always parses markdown, groups successive references
 * Always uses drawer for references
 */
export const TextWithReferences = ({
  content,
  referencesDict,
  onReferenceClick,
}: {
  content: string;
  referencesDict?: ReferencesDict;
  onReferenceClick?: (reference: Reference) => void;
}) => {
  const [searchParams] = useSearchParams();
  const { matterId } = useParams();
  const caseId = matterId ?? searchParams.get('caseId');
  const [formattedContent, setFormattedContent] = useState('');

  useEffect(() => {
    const processContent = async () => {
      if (!content) return;

      let htmlContent = await marked.parse(content);
      const olRegex = /<ol>/g;
      if (olRegex.test(htmlContent)) {
        htmlContent = htmlContent.replace(/<ol>/g, '<ol className="list-decimal ml-6">');
      }
      setFormattedContent(htmlContent);
    };

    processContent();
  }, [content]);

  return processReferences(formattedContent, { referencesDict, onReferenceClick, caseId });
};

// Internal function to process references in HTML content
const processReferences = (
  htmlContent: string,
  options: {
    caseId: string | null;
    referencesDict?: ReferencesDict;
    onReferenceClick?: (reference: Reference) => void;
  },
): React.ReactNode => {
  const { openReferenceDrawer } = useStore();
  const { referencesDict, onReferenceClick, caseId } = options || {};

  // Early return if no case ID provided
  if (!caseId) {
    return htmlContent;
  }

  const handleReferenceClick = (reference: Reference) => {
    if (reference.type === 'chunk') {
      openReferenceDrawer({ chunkId: reference.id, caseId });
    } else {
      openReferenceDrawer({ factId: reference.id, caseId });
    }
    onReferenceClick?.(reference);
  };

  // Function to create reference elements
  const createReferenceElement = (referenceNum: number, index: number, id: string): ReactElement => {
    const ref = referencesDict?.[referenceNum];
    const isRefChunk = ref?.type === 'chunk';

    // Use orange for chunk references, blue for fact references
    const colorClasses = isRefChunk
      ? 'cursor-pointer text-orange-500 hover:text-orange-700'
      : 'cursor-pointer text-blue-500 hover:text-blue-700';

    return (
      <span
        key={`reference-${referenceNum}-${index}`}
        className={colorClasses}
        onClick={() => handleReferenceClick({ id: ref?.id || id, type: ref?.type || 'fact' })}
      >
        [{referenceNum}]
      </span>
    );
  };

  // Parse HTML and replace references
  const parserOptions: HTMLReactParserOptions = {
    replace: (domNode: any) => {
      if (domNode.type === 'text') {
        const text = domNode.data;
        const elements: React.ReactNode[] = [];
        let lastIndex = 0;

        // Find all references in the text
        for (const match of text.matchAll(referencingLinkRegex)) {
          // Add text before the reference
          if (match.index !== undefined && match.index > lastIndex) {
            elements.push(text.slice(lastIndex, match.index));
          }

          // Single reference number
          const referenceNum = parseInt(match[1], 10);
          const id = match[2]; // From the regex capture group

          elements.push(createReferenceElement(referenceNum, match.index!, id));

          lastIndex = match.index! + match[0].length;
        }

        // Add remaining text
        if (lastIndex < text.length) {
          elements.push(text.slice(lastIndex));
        }

        // Only return if we found references
        if (elements.length > 0 && lastIndex > 0) {
          return <span className="inline">{elements}</span>;
        }
      }
      return domNode;
    },
  };

  const parsed = parse(htmlContent, parserOptions);
  return sweepAndMergeReferences(parsed);
};

// Constants for reference grouping
const REFERENCE_GROUP_THRESHOLD = 4;

// Helper function to check if a child is a reference element
const isReferenceElement = (child: React.ReactNode): boolean => {
  return (
    React.isValidElement(child) &&
    Boolean(child.key) &&
    typeof child.key === 'string' &&
    child.key.startsWith('.$reference-')
  );
};

// Helper function to process a group of references
const processReferenceGroup = (referenceGroup: React.ReactNode[], groupIndex: number): React.ReactNode => {
  if (referenceGroup.length > REFERENCE_GROUP_THRESHOLD) {
    return <ReferencesGroup key={`group-${groupIndex}`} references={[...referenceGroup]} />;
  }
  return referenceGroup;
};

// Function for grouping consecutive references to improve readability
const sweepAndMergeReferences = (content: string | JSX.Element | JSX.Element[]): React.ReactNode => {
  // Handle primitive content types
  if (typeof content === 'string') {
    return content;
  }

  if (Array.isArray(content)) {
    return content.map((item) => sweepAndMergeReferences(item));
  }

  // Handle JSX elements
  if (
    !React.isValidElement(content) ||
    !('children' in (content.props as any)) ||
    (content.props as any).children === undefined ||
    (content.props as any).children === null
  ) {
    return content;
  }

  const children = React.Children.toArray((content.props as any).children as React.ReactNode);
  const updatedChildren: React.ReactNode[] = [];
  let consecutiveReferences: React.ReactNode[] = [];

  // Process each child element
  children.forEach((child, index) => {
    if (child == null || child == undefined || (typeof child === 'string' && child.trim() === '')) {
      return;
    }

    if (isReferenceElement(child)) {
      // Add reference to current group
      consecutiveReferences.push(child);
    } else {
      // Non-reference element found - process any accumulated references
      if (consecutiveReferences.length > 0) {
        updatedChildren.push(processReferenceGroup(consecutiveReferences, index));
        consecutiveReferences = [];
      }

      // Process the non-reference child recursively
      updatedChildren.push(sweepAndMergeReferences(child as JSX.Element));
    }
  });

  // Handle any remaining references at the end
  if (consecutiveReferences.length > 0) {
    updatedChildren.push(processReferenceGroup(consecutiveReferences, children.length));
  }

  // Reconstruct the element with processed children
  return React.cloneElement(content as React.ReactElement<any>, {
    ...(content.props as any),
    children: updatedChildren,
  });
};
